import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { RulesLogic } from 'json-logic-js';
import { DateTime } from 'luxon';
import { useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { Helmet } from 'react-helmet-async';
import { Route } from 'react-router-dom-v5-compat';

import { useCodeRunner } from 'dashboard/components/CodeRunner/CodeRunner';
import { GridLayoutRender } from 'dashboard/components/GridLayoutRender/GridLayoutRender';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { AddCaseCommentFormData } from 'dashboard/forms/FormAddCaseComment';
import { AssignCaseFormData } from 'dashboard/forms/FormAssignCase';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { FormSendCaseEmailData } from 'dashboard/forms/FormSendCaseEmail';
import { FormSetCaseStateData } from 'dashboard/forms/FormSetCaseState';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Backdrop, HeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { NextPageSetting } from 'dashboard/lib/constants/case';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { Case, CaseActionWorkflow, CaseDataManager, CaseObject } from 'dashboard/models/case';
import { CaseComment } from 'dashboard/models/case-comment';
import { CaseMessage } from 'dashboard/models/case-message';
import { CaseQueryResult } from 'dashboard/models/case-query-result';
import {
  CaseCommentTemplate,
  CaseEmailTemplate,
  CaseState,
  CaseTemplate,
  SARNarrativeTemplate,
} from 'dashboard/models/case-template';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { Device } from 'dashboard/models/device';
import { DocumentGeneric } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { ReportTemplate } from 'dashboard/models/report-template';
import { SARData } from 'dashboard/models/sar';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { CaseModuleBase } from 'dashboard/modules/CaseModuleBase/CaseModuleBase';
import { useCaseDataContext } from 'dashboard/routes/DashboardCaseShowRoute/useCaseDataContext';
import { CaseDetailsPrint } from 'dashboard/views/DashboardCaseShowView/CaseDetailsPrint';
import CaseHeader from 'dashboard/views/DashboardCaseShowView/CaseHeader';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { FieldSchema } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Iso8601DateString, Json } from 'lib/types';
import { PreparedFileUpload } from 'lib/utils/dataUrl';
import { runJsonLogic } from 'lib/utils/jsonLogicRunner';

export interface CaseApi {
  accounts: Account[];
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  allInquiryTemplates: (Template | InquiryTemplate)[];
  assignee: User | undefined;
  case: Case;
  caseActionWorkflows: CaseActionWorkflow[];
  caseComments: CaseComment[];
  caseCommentTemplates: CaseCommentTemplate[];
  caseDataManager: CaseDataManager;
  caseEmailTemplates: CaseEmailTemplate[];
  caseMessages: CaseMessage[];
  caseObjects: CaseObject[];
  caseQueryResults: CaseQueryResult[];
  caseTemplateFieldSchema: FieldSchema[];
  caseTemplateStates: CaseState[];
  commentWorkflowRuns: WorkflowRun[];
  commentWorkflows: Workflow[];
  commentWorkflowStepActions: WorkflowRunStepAction[];
  currentEnvironment: Environment;
  devices: Device[];
  documents: DocumentGeneric[];
  featureFlags: FeatureFlagsMap;
  inquiries: Inquiry[];
  inquiryTemplates: (Template | InquiryTemplate)[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  inquiryVerifications: Verification[];
  isLoadingInquiriesDetails: boolean;
  isLoadingReportsDetails: boolean;
  listItems: ListItem[];
  listMatches: ListMatch[];
  lists: List[];
  reportTemplates: ReportTemplate[];
  sarNarrativeTemplates: SARNarrativeTemplate[];
  sessions: InquirySession[];
  tags: Tag[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
  users: User[];
  verifications: Verification[];
  addComment: (values: AddCaseCommentFormData, mentionedUsers: string[]) => Promise<void>;
  editCaseComment: (message: string, id: string, mentionedUsers: string[]) => Promise<void>;
  removeCaseComment: (id: string) => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleUpdateInquiryTags: (inquiryId: string, tags: string[]) => void;
  handleUpdateReportTags: (reportId: string, tags: string[]) => void;
  handleAddTags: (accountIds: string[], tags: FormUpdateTagsData) => void;
  handleAddCaseAttachment: (attachments: PreparedFileUpload[]) => void;
  handleDeleteCaseAttachment: (attachmentKey: string) => void;
  handleToggleChecklistItem: (itemId: string) => void;
  handleUpdateFields: (fields: Record<string, any>) => void; // this should probably actually be string, Field or something
  handleBulkUpdateAccountStatus: (accountIds: string[], accountStatusId: string) => void;
  handleRefreshCaseQueryResult: (caseQueryResult: CaseQueryResult) => void;
  handleRunCaseActionWorkflow: (values: Json, workflowId: string) => Promise<void>;
  handleCreateEmailDraft: ({
    to,
    from,
    body,
    threadId,
  }: {
    to: string[];
    from: string;
    body: string;
    threadId?: string;
  }) => Promise<void>;
  handleUpdateEmailDraft: (values: FormSendCaseEmailData) => Promise<void>;
  handleSendEmail: (values: FormSendCaseEmailData) => Promise<void>;
  handleDeleteEmail: (id: string) => Promise<void>;
  handleCreateSAR: () => Promise<void>;
  handleUpdateSAR: (sarId: string, sarData: SARData) => Promise<void>;
  handleAutofillSAR: (sarId: string) => Promise<void>;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleGenerateSummary: (userPrompt: string) => Promise<string>;
  showMessage: (message: string) => void;
}
export interface DashboardCaseShowViewProps {
  currentEnvironment: Environment;
  case: Case | undefined; // note that this is undefined when the network request has not completed
  caseDataManager: CaseDataManager | undefined; // note that this is undefined when the network request has not completed
  caseTemplate: CaseTemplate | undefined; // note that this is undefined when the network request has not completed
  caseQueryResults: CaseQueryResult[];
  caseVariableDefinitions: CaseVariableDefinition[];
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  actionWorkflows: CaseActionWorkflow[];
  assignee: User | undefined;
  objects: CaseObject[];
  users: User[];
  templates: (InquiryTemplate | Template)[];
  enabledTemplates: (InquiryTemplate | Template)[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  inquiryVerifications: Verification[];
  verifications: Verification[];
  devices: Device[];
  documents: DocumentGeneric[];
  caseComments: CaseComment[];
  caseMessages: CaseMessage[];
  commentWorkflows: Workflow[];
  commentWorkflowStepActions: WorkflowRunStepAction[];
  commentWorkflowRuns: WorkflowRun[];
  reportTemplates: ReportTemplate[];
  sessions: InquirySession[];
  tags: Tag[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
  allTags: Tag[];
  lists: List[];
  listMatches: ListMatch[];
  listItems: ListItem[];
  nextPageSettingDefault: NextPageSetting;
  isLoading: boolean;
  isLoadingInquiriesDetails: boolean;
  isLoadingReportsDetails: boolean;
  hasCasePermissionError: boolean;
  featureFlags: FeatureFlagsMap;
  addComment: (values: AddCaseCommentFormData, mentionedUsers: string[]) => Promise<void>;
  editCaseComment: (message: string, id: string, mentionedUsers: string[]) => Promise<void>;
  removeCaseComment: (id: string) => Promise<void>;
  assignCase: (values: AssignCaseFormData) => Promise<void>;
  handleToggleChecklistItem: (itemId: string) => Promise<void>;
  handleUpdateFields: (fields: Record<string, any>, options?: { showToast?: boolean }) => void;
  handleRefreshCaseQueryResult: (caseQueryResult: CaseQueryResult) => void;
  handleRunCaseActionWorkflow: (values: Json, workflowId: string) => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => Promise<void>;
  handleUpdateInquiryTags: (inquiryId: string, tags: string[]) => Promise<void>;
  handleUpdateReportTags: (reportId: string, tags: string[]) => Promise<void>;
  handleSetCaseState: (stateId: string, values: FormSetCaseStateData) => Promise<void>;
  handleAddTags: (accountIds: string[], tags: FormUpdateTagsData) => void;
  handleAddCaseAttachment: (attachments: PreparedFileUpload[]) => void;
  handleDeleteCaseAttachment: (attachmentKey: string) => void;
  handleBulkUpdateAccountStatus: (accountIds: string[], accountStatusId: string) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleCreateEmailDraft: ({
    to,
    from,
    body,
    threadId,
  }: {
    to: string[];
    from: string;
    body: string;
    threadId?: string;
  }) => Promise<void>;
  handleUpdateEmailDraft: (values: FormSendCaseEmailData) => Promise<void>;
  handleSendEmail: (values: FormSendCaseEmailData) => Promise<void>;
  handleDeleteEmail: (id: string) => Promise<void>;
  handleRedactCase: (id: string) => Promise<void>;
  handleCreateSAR: () => Promise<void>;
  handleUpdateSAR: (sarId: string, sarData: SARData) => Promise<void>;
  handleAutofillSAR: (sarId: string) => Promise<void>;
  handleGenerateSummary: (userPrompt: string) => Promise<string>;
  showMessage: (message: string) => void;
}

const DashboardCaseShowView = ({
  currentEnvironment,
  case: kase,
  caseDataManager,
  caseTemplate,
  caseQueryResults,
  caseVariableDefinitions,
  assignee,
  objects,
  caseComments,
  caseMessages,
  commentWorkflows,
  accountStatuses,
  accountTypes,
  commentWorkflowStepActions,
  commentWorkflowRuns,
  actionWorkflows,
  reportTemplates,
  sessions,
  tags,
  transactionStatuses,
  transactionTypes,
  allTags,
  users,
  lists,
  listMatches,
  listItems,
  templates,
  enabledTemplates,
  inquiryTemplateVersions,
  inquiryVerifications,
  verifications,
  devices,
  documents,
  nextPageSettingDefault,
  isLoading,
  isLoadingInquiriesDetails,
  isLoadingReportsDetails,
  hasCasePermissionError,
  featureFlags,
  addComment,
  editCaseComment,
  removeCaseComment,
  assignCase,
  handleRefreshCaseQueryResult,
  handleRunCaseActionWorkflow,
  handleToggleChecklistItem,
  handleUpdateFields,
  handleUpdateTags,
  handleSetCaseState,
  handleAddTags,
  handleUpdateInquiryTags,
  handleUpdateReportTags,
  handleAddCaseAttachment,
  handleDeleteCaseAttachment,
  handleBulkUpdateAccountStatus,
  handleFlagInquiryIssues,
  handleFlagVerificationIssues,
  handleCreateEmailDraft,
  handleUpdateEmailDraft,
  handleSendEmail,
  handleDeleteEmail,
  handleRedactCase,
  handleCreateSAR,
  handleUpdateSAR,
  handleAutofillSAR,
  handleGenerateSummary,
  showMessage,
}: DashboardCaseShowViewProps) => {
  const caseApiRef = useRef<CaseApi | null>(null);
  useCaseDataContext({
    caseId: kase?.id || '',
    variableDefinitions: caseVariableDefinitions,
    isPreview: false,
  });
  const { runCode } = useCodeRunner();

  const handleRunCodeWithContext = useCallback(
    (code: string) => {
      return runCode(code, caseDataManager ?? {});
    },
    [caseDataManager, runCode],
  );

  const handleRunJsonLogicWithContext = useCallback(
    async (rules: RulesLogic) => {
      return runJsonLogic(rules, caseDataManager);
    },
    [caseDataManager],
  );

  if (kase == null || caseTemplate == null || caseDataManager == null || isLoading) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message={
            hasCasePermissionError
              ? 'You do not have permission to view this Case.'
              : 'We couldn’t find that Case.'
          }
          backRoute='/dashboard/cases'
        />
      </Backdrop>
    );
  }

  const accounts = objects.filter((object) => object.type === ApiModel.Account) as Account[];
  const inquiries = objects.filter((object) => object.type === ApiModel.Inquiry) as Inquiry[];

  const isRedacted = kase.attributes['redacted-at'] != null;

  const redactor: User | undefined =
    isRedacted && kase.relationships.redactor.data != null
      ? users.find((user) => user.id === kase.relationships.redactor.data.id)
      : undefined;
  const redactorName = redactor == null ? 'via API' : `by ${userDisplayName(redactor)}`;
  const bannerText = `This case was redacted ${redactorName} on ${DateTime.fromISO(
    kase.attributes['redacted-at'] as Iso8601DateString,
  ).toFormat(DATE_AND_TIME_FORMAT)}.`;

  const caseTemplateStates = caseTemplate.attributes.configuration.states;
  const caseTemplateFieldSchema = caseTemplate.attributes.configuration['field-schema'];
  const caseEmailTemplates = caseTemplate.attributes.configuration['case-message-templates'];
  const caseCommentTemplates = caseTemplate.attributes.configuration['case-comment-templates'];
  const sarNarrativeTemplates = caseTemplate.attributes.configuration['sar-narrative-templates'];

  const rawCaseApi = {
    accounts,
    accountStatuses,
    accountTypes,
    allInquiryTemplates: enabledTemplates,
    assignee,
    case: kase,
    caseActionWorkflows: actionWorkflows,
    caseComments,
    caseCommentTemplates,
    caseDataManager,
    caseEmailTemplates,
    caseMessages,
    caseObjects: objects,
    caseQueryResults,
    caseTemplateFieldSchema,
    caseTemplateStates,
    caseVariableDefinitions,
    commentWorkflowRuns,
    commentWorkflows,
    commentWorkflowStepActions,
    currentEnvironment,
    devices,
    documents,
    featureFlags,
    inquiries,
    inquiryTemplates: templates,
    inquiryTemplateVersions,
    inquiryVerifications,
    isLoadingInquiriesDetails,
    isLoadingReportsDetails,
    listItems,
    listMatches,
    lists,
    reportTemplates,
    sarNarrativeTemplates,
    sessions,
    tags,
    transactionStatuses,
    transactionTypes,
    users: users,
    verifications,
    addComment,
    editCaseComment,
    handleAddCaseAttachment,
    handleAddTags,
    handleAutofillSAR,
    handleBulkUpdateAccountStatus,
    handleCreateEmailDraft,
    handleCreateSAR,
    handleDeleteCaseAttachment,
    handleDeleteEmail,
    handleFlagInquiryIssues,
    handleFlagVerificationIssues,
    handleRefreshCaseQueryResult,
    handleRunCaseActionWorkflow,
    handleSendEmail,
    handleToggleChecklistItem,
    handleUpdateEmailDraft,
    handleUpdateFields,
    handleUpdateInquiryTags,
    handleUpdateReportTags,
    handleUpdateSAR,
    handleUpdateTags,
    handleGenerateSummary,
    removeCaseComment,
    showMessage,
  };
  // React is bad at object equality (does equality by reference by default) so we use deep equality
  // to determine if we should pass a new instance of caseApi down the tree
  if (!isEqual(rawCaseApi, caseApiRef.current)) {
    caseApiRef.current = rawCaseApi;
  }
  const caseApi = caseApiRef.current;

  if (!caseApi) return null;

  return (
    <>
      <Helmet title={`${kase.attributes.name} - Cases`} />
      <SentryRoutes>
        <Route
          path='print'
          element={
            <CaseDetailsPrint
              dataApi={caseApi}
              configuration={caseTemplate.attributes.configuration['case-layout-v2']}
              runCodeWithContext={handleRunCodeWithContext}
              runJsonLogicWithContext={handleRunJsonLogicWithContext}
            />
          }
        />
        <Route
          index
          path='*'
          element={
            <>
              <InactiveEnvironmentBanner />
              {isRedacted && <Banner icon={<CircleAlertIcon />}>{bannerText}</Banner>}
              <Backdrop data-test='case-show-view'>
                <HeaderContainer data-test='case-show-view__header'>
                  <CaseHeader
                    case={kase}
                    caseDataManager={caseDataManager}
                    caseTemplate={caseTemplate}
                    currentEnvironment={currentEnvironment}
                    actionWorkflows={actionWorkflows}
                    accounts={accounts}
                    inquiries={inquiries}
                    templates={templates}
                    tags={allTags}
                    verifications={verifications}
                    users={users}
                    nextPageSettingDefault={nextPageSettingDefault}
                    handleSetCaseState={handleSetCaseState}
                    handleRunCaseActionWorkflow={handleRunCaseActionWorkflow}
                    assignCase={assignCase}
                    handleRedactCase={handleRedactCase}
                  />
                </HeaderContainer>
                <GridLayoutRender
                  configuration={caseTemplate.attributes.configuration['case-layout-v2']}
                  renderModule={(module) => {
                    return (
                      <CaseModuleBase
                        module={module}
                        caseApi={caseApi}
                        runCodeWithContext={handleRunCodeWithContext}
                      />
                    );
                  }}
                  runCodeWithContext={handleRunCodeWithContext}
                  runJsonLogicWithContext={handleRunJsonLogicWithContext}
                />
              </Backdrop>
            </>
          }
        />
      </SentryRoutes>
    </>
  );
};

export default DashboardCaseShowView;
